import { ReplicateApi } from '@/api';
import { ContentApi } from '@/api';
import fileUpload from '@/utils/fileUpload';

// Load only active processing generations on page reload - clean slate approach
const loadGenerationsFromStorage = () => {
  try {
    const saved = localStorage.getItem('video-generations');
    if (saved) {
      const parsed = JSON.parse(saved);
      const now = Date.now();
      const fifteenMinutesAgo = now - 15 * 60 * 1000; // Replicate timeout

      // On page reload, ONLY keep genuinely active processing items (within 15 minutes)
      const activeProcessingItems = parsed.filter(gen => {
        // Must be valid object with required fields
        if (!gen || typeof gen !== 'object' || !gen.id || !gen.startTime) {
          return false;
        }

        // Must have valid progress
        if (typeof gen.progress !== 'number' || isNaN(gen.progress) || gen.progress < 0) {
          return false;
        }

        // Must have valid start time
        const startTime = new Date(gen.startTime).getTime();
        if (isNaN(startTime) || startTime < fifteenMinutesAgo) {
          return false;
        }

        // ONLY keep items that are actively processing (not completed, failed, etc.)
        return (gen.status === 'processing' || gen.status === 'starting') && gen.progress < 100;
      });

      console.log(`Page reload: Kept ${activeProcessingItems.length} active processing items out of ${parsed.length} total`);
      return activeProcessingItems;
    }
    return [];
  } catch (error) {
    console.error('Error loading video generations from localStorage:', error);
    // Clear corrupted localStorage data
    try {
      localStorage.removeItem('video-generations');
    } catch (clearError) {
      console.error('Error clearing corrupted localStorage:', clearError);
    }
    return [];
  }
};

// Save generations to localStorage for session persistence
const saveGenerationsToStorage = generations => {
  try {
    // Validate generations before saving
    const validGenerations = generations.filter(gen => {
      // Basic validation - ensure required fields and valid progress
      return gen && typeof gen === 'object' && gen.id && gen.startTime && typeof gen.progress === 'number' && !isNaN(gen.progress) && gen.progress >= 0;
    });

    // Only save if we have valid data
    if (validGenerations.length !== generations.length) {
      console.warn(`Filtered out ${generations.length - validGenerations.length} invalid generations before saving`);
    }

    // Persist video generations to survive navigation and page refresh
    localStorage.setItem('video-generations', JSON.stringify(validGenerations));
  } catch (error) {
    console.error('Error saving video generations to localStorage:', error);
  }
};

const defaultState = () => {
  const activeProcessingItems = loadGenerationsFromStorage(); // Only loads active processing items

  return {
    generations: activeProcessingItems,
    isVisible: activeProcessingItems.length > 0, // Auto-show if there are active processing items
    lastUpdate: activeProcessingItems.length > 0 ? Date.now() : null, // Timestamp of last update
    progressTimer: null, // Timer for real-time progress updates
    statusPollingTimer: null // Timer for polling server status updates
  };
};

const videoGenerationProgress = {
  namespaced: true,
  state: defaultState(),

  mutations: {
    resetState(state) {
      Object.assign(state, defaultState());
    },

    setVisible(state, isVisible) {
      state.isVisible = isVisible;
    },

    addGeneration(state, generation) {
      // Check if generation already exists
      const existingIndex = state.generations.findIndex(g => g.id === generation.id);

      if (existingIndex !== -1) {
        // Update existing generation
        state.generations.splice(existingIndex, 1, { ...state.generations[existingIndex], ...generation });
      } else {
        // Add new generation to the beginning of array
        state.generations.unshift(generation);
      }

      state.lastUpdate = Date.now();
      saveGenerationsToStorage(state.generations);
    },

    updateGeneration(state, { id, data }) {
      const index = state.generations.findIndex(g => g.id === id);
      if (index !== -1) {
        state.generations.splice(index, 1, { ...state.generations[index], ...data });
        state.lastUpdate = Date.now();
        saveGenerationsToStorage(state.generations);
      }
    },

    removeGeneration(state, id) {
      const index = state.generations.findIndex(g => g.id === id);
      if (index !== -1) {
        state.generations.splice(index, 1);
        state.lastUpdate = Date.now();
        saveGenerationsToStorage(state.generations);
      }
    },

    clearCompleted(state) {
      // Remove all completed items immediately
      state.generations = state.generations.filter(g => g.progress < 100 && g.status !== 'error' && g.status !== 'completed');
      state.lastUpdate = Date.now();
      saveGenerationsToStorage(state.generations);
    },

    clearAll(state) {
      state.generations = [];
      state.lastUpdate = Date.now();
      saveGenerationsToStorage(state.generations);
    },

    cleanupExpiredGenerations(state) {
      const now = Date.now();
      const FIFTEEN_MINUTES = 15 * 60 * 1000; // 15 minutes in milliseconds
      const FIVE_MINUTES = 5 * 60 * 1000; // 5 minutes in milliseconds
      const TWENTY_MINUTES = 20 * 60 * 1000; // 20 minutes in milliseconds

      // Remove generations with comprehensive cleanup
      state.generations = state.generations.filter(generation => {
        // Check for invalid data structure
        if (!generation || typeof generation !== 'object') {
          console.warn('Cleaning up invalid generation object:', generation);
          return false;
        }

        // Check for required fields
        if (!generation.id || !generation.startTime) {
          console.warn('Cleaning up generation with missing required fields:', generation);
          return false;
        }

        // Check for invalid progress values (NaN, negative, etc.)
        if (typeof generation.progress !== 'number' || isNaN(generation.progress) || generation.progress < 0) {
          console.warn('Cleaning up generation with invalid progress:', generation.progress, generation);
          return false;
        }

        const startTime = new Date(generation.startTime).getTime();
        if (isNaN(startTime)) {
          console.warn('Cleaning up generation with invalid startTime:', generation.startTime, generation);
          return false;
        }

        const elapsed = now - startTime;

        // Remove all generations older than 20 minutes (regardless of status)
        if (elapsed > TWENTY_MINUTES) {
          console.log('Cleaning up generation older than 20 minutes:', generation);
          return false;
        }

        // Keep completed generations (within 20 minutes)
        if (generation.progress >= 100 || generation.status === 'completed') {
          return true;
        }

        // Remove failed generations older than 5 minutes
        if ((generation.status === 'error' || generation.status === 'failed') && elapsed > FIVE_MINUTES) {
          console.log('Cleaning up old failed generation:', generation);
          return false;
        }

        // Remove processing generations older than 15 minutes (Replicate timeout)
        if ((generation.status === 'processing' || generation.status === 'starting') && elapsed > FIFTEEN_MINUTES) {
          console.log('Cleaning up expired processing generation:', generation);
          return false;
        }

        return true;
      });

      state.lastUpdate = now;
      saveGenerationsToStorage(state.generations);
    },

    // Note: Progress is now calculated and synced directly from VideoGenerator component
    // This mutation is kept for backward compatibility but is no longer actively used

    updateGenerationsFromStatus(state, statusArray) {
      if (!Array.isArray(statusArray)) return;

      statusArray.forEach(statusItem => {
        // Handle both individual API format (predictionId) and global API format (id as predictionId)
        const predictionId = statusItem.predictionId || statusItem.id;
        const generation = state.generations.find(g => g.predictionId === predictionId);
        if (generation) {
          // Store the previous status to detect newly completed generations
          const wasCompleted = generation.status === 'completed' || generation.progress >= 100;

          // Always trust server status - if server still shows "processing", it's still active
          if (statusItem.status === 'succeeded') {
            generation.progress = 100;
            generation.status = 'completed';
            generation.videoUrl = statusItem.output;
            generation.message = 'Video generated successfully';

            // If this is a newly completed generation, handle completion centrally
            if (!wasCompleted && statusItem.output) {
              // Use setTimeout to defer the completion handling to avoid mutation conflicts
              setTimeout(() => {
                this.dispatch('videoGenerationProgress/handleGenerationCompletion', {
                  id: generation.id,
                  videoUrl: statusItem.output,
                  message: 'Video generated successfully',
                  isActivelyManaged: generation.isActivelyManaged || false
                });
              }, 100);
            }
          } else if (statusItem.status === 'failed' || statusItem.status === 'canceled') {
            generation.status = 'error';
            generation.message = statusItem.error || 'Generation failed';
          } else if (statusItem.status === 'processing' || statusItem.status === 'starting') {
            // Always update status to match server - server is source of truth
            generation.status = statusItem.status;
            // Clear any previous error messages if server shows active status
            if (generation.message && generation.message.includes('expired')) {
              generation.message = null;
            }
          }
        }
      });

      state.lastUpdate = Date.now();
      saveGenerationsToStorage(state.generations);
    },

    // Update real-time progress for all active generations (same logic as VideoGenerator)
    updateRealTimeProgress(state) {
      const now = Date.now();
      const progressDuration = 240000; // 4 minutes in milliseconds

      state.generations.forEach(generation => {
        // Only update progress for active generations that aren't currently being updated by VideoGenerator
        // If VideoGenerator is actively managing, it will override these values anyway
        if ((generation.status === 'processing' || generation.status === 'starting') && generation.progress < 100) {
          const startTimeMs = generation.progressStartTime || new Date(generation.startTime).getTime();
          const elapsed = now - startTimeMs;

          // Calculate real-time progress (same logic as VideoGenerator)
          let progress = (elapsed / progressDuration) * 100;

          if (progress >= 95 && generation.status === 'processing') {
            // Slow down progress significantly after 95%
            const overtime = elapsed - progressDuration * 0.95;
            const slowProgress = 95 + (overtime / (progressDuration * 0.5)) * 4;
            progress = Math.min(slowProgress, 99);
          } else {
            progress = Math.min(progress, 95);
          }

          // Only update if the calculated progress is greater than current progress
          // This prevents regression when VideoGenerator has already advanced the progress
          if (progress > generation.progress) {
            generation.progress = Math.max(0, progress);
          }

          // Ensure we have progressStartTime property
          generation.progressStartTime = startTimeMs;
        }
      });

      state.lastUpdate = now;
      saveGenerationsToStorage(state.generations);
    }
  },

  actions: {
    resetState({ commit, dispatch }) {
      dispatch('stopProgressTimer'); // Clean up timer before reset
      dispatch('stopStatusPolling'); // Clean up status polling timer before reset
      commit('resetState');
    },

    showDialog({ commit }) {
      commit('setVisible', true);
    },

    hideDialog({ commit }) {
      commit('setVisible', false);
    },

    addGeneration({ commit, dispatch }, generationData) {
      // Create generation object with required properties
      const generation = {
        id: generationData.id || generationData.predictionId || `gen_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        predictionId: generationData.predictionId,
        fileName: generationData.fileName || 'AI Video Generation',
        cameraEffect: generationData.cameraEffect || 'Unknown Effect',
        imageUrl: generationData.imageUrl,
        progress: generationData.progress || 0,
        status: generationData.status || 'starting',
        startTime: generationData.startTime || new Date().toISOString(),
        progressStartTime: generationData.progressStartTime, // Preserve the progressStartTime!
        message: generationData.message || null,
        videoUrl: generationData.videoUrl || null,
        isActivelyManaged: generationData.isActivelyManaged || false
      };

      commit('addGeneration', generation);

      // Only show dialog for new generations (not restored from localStorage)
      if (!generationData.isRestored) {
        dispatch('showDialog');
      }

      // Start progress timer for real-time updates
      dispatch('startProgressTimer');

      // Start status polling to detect completion
      dispatch('startStatusPolling');

      return generation;
    },

    updateGeneration({ commit }, { id, data }) {
      commit('updateGeneration', { id, data });
    },

    removeGeneration({ commit }, id) {
      commit('removeGeneration', id);
    },

    clearCompleted({ commit }) {
      commit('clearCompleted');
    },

    clearAll({ commit }) {
      commit('clearAll');
    },

    cleanupExpiredGenerations({ commit }) {
      commit('cleanupExpiredGenerations');
    },

    markExpiredAsError({ commit }) {
      commit('markExpiredAsError');
    },

    // Initialize and cleanup expired generations (call this on app startup)
    initializeAndCleanup({ dispatch }) {
      // Clean up old expired/failed generations (no longer marking as expired based on time)
      dispatch('cleanupExpiredGenerations');
    },

    updateGenerationsFromStatus({ commit }, statusData) {
      commit('updateGenerationsFromStatus', statusData);
    },

    // Note: Progress is now calculated and synced directly from VideoGenerator component
    // This action is kept for backward compatibility but no longer updates progress

    async getGenerationStatus({ state }) {
      try {
        // Get generations that need status checking
        const generationsNeedingCheck = state.generations.filter(g => {
          return g.predictionId && g.status !== 'error' && g.progress < 100;
        });

        if (generationsNeedingCheck.length === 0) {
          return [];
        }

        // Check status for each generation
        const statusPromises = generationsNeedingCheck.map(async generation => {
          try {
            const response = await ReplicateApi.getPredictionStatus(generation.predictionId);
            return {
              predictionId: generation.predictionId,
              status: response.data.prediction?.status,
              output: response.data.prediction?.output,
              error: response.data.prediction?.error
            };
          } catch (error) {
            console.error(`Error checking status for generation ${generation.id}:`, error);
            return {
              predictionId: generation.predictionId,
              status: 'error',
              error: 'Failed to check status'
            };
          }
        });

        const statusResults = await Promise.all(statusPromises);
        return statusResults.filter(result => result !== null);
      } catch (error) {
        console.error('Error in getGenerationStatus:', error);
        return [];
      }
    },

    // Helper action to start tracking a new video generation
    startVideoGeneration({ dispatch }, { predictionId, imageUrl, cameraEffect, fileName }) {
      const now = Date.now();
      const generationData = {
        id: predictionId, // Use predictionId as the generation ID for easier tracking
        predictionId,
        imageUrl,
        cameraEffect,
        fileName: fileName,
        progress: 0,
        status: 'starting',
        startTime: new Date().toISOString(),
        progressStartTime: now // Store start time in milliseconds for real-time calculations
      };

      const generation = dispatch('addGeneration', generationData);

      // Start progress timer for real-time updates
      dispatch('startProgressTimer');

      // Start status polling to detect completion
      dispatch('startStatusPolling');

      return generation;
    },

    // Start real-time progress updates for active generations
    startProgressTimer({ commit, state }) {
      // Clear any existing interval
      if (state.progressTimer) {
        clearInterval(state.progressTimer);
        state.progressTimer = null;
      }

      // Start progress timer (update every 100ms like VideoGenerator)
      state.progressTimer = setInterval(() => {
        commit('updateRealTimeProgress');
      }, 100);
    },

    // Stop real-time progress updates
    stopProgressTimer({ state }) {
      if (state.progressTimer) {
        clearInterval(state.progressTimer);
        state.progressTimer = null;
      }
    },

    // Check if we need to restart the timer for active generations (call this on page navigation)
    ensureProgressTimer({ dispatch, state }) {
      const activeGenerations = state.generations.filter(g => (g.status === 'processing' || g.status === 'starting') && g.progress < 100 && g.status !== 'error');

      const hasActiveGenerations = activeGenerations.length > 0;
      const hasTimer = !!state.progressTimer;
      const hasStatusPolling = !!state.statusPollingTimer;

      if (hasActiveGenerations && !hasTimer) {
        dispatch('startProgressTimer');
      } else if (!hasActiveGenerations && hasTimer) {
        dispatch('stopProgressTimer');
      }

      if (hasActiveGenerations && !hasStatusPolling) {
        dispatch('startStatusPolling');
      } else if (!hasActiveGenerations && hasStatusPolling) {
        dispatch('stopStatusPolling');
      }
    },

    // Add a centralized method to handle video generation completion with auto-upload
    async handleGenerationCompletion({ dispatch, state }, { id, videoUrl, message, isActivelyManaged = false }) {
      // Update generation status
      dispatch('updateGeneration', {
        id,
        data: {
          progress: 100,
          status: 'completed',
          videoUrl,
          message: message || 'Video generated successfully'
        }
      });

      // Auto-upload the video if it completed in the background (not actively managed by VideoGenerator)
      const generation = state.generations.find(g => g.id === id);

      if (generation && !isActivelyManaged) {
        // Use the centralized auto-upload function with deduplication
        setTimeout(async () => {
          try {
            const uploadedContent = await fileUpload.autoUploadGeneratedVideo(
              videoUrl,
              {
                cameraEffect: generation.cameraEffect,
                fileName: `ai-generated-video-${generation.cameraEffect.replace(/\s+/g, '-').toLowerCase()}-${Date.now()}.mp4`
              },
              { dispatch: (action, payload) => dispatch(action, payload, { root: true }) }
            );
          } catch (error) {
            console.error('Auto-upload failed for generation:', error);
          }
        }, 100);
      }

      // Check if all generations are completed and stop timers if so
      const hasActiveGenerations = state.generations.some(g => g.status === 'processing' || g.status === 'starting' || (g.progress < 100 && g.status !== 'error'));

      if (!hasActiveGenerations) {
        dispatch('stopProgressTimer');
        dispatch('stopStatusPolling');
      }
    },

    // Centralized progress management - VideoGenerator should call this instead of managing its own progress
    updateProgressForGeneration({ commit, state }, { id, progress, elapsed, isActivelyManaged = true }) {
      const generation = state.generations.find(g => g.id === id);
      if (!generation) return;

      // Update the generation with real progress values from VideoGenerator
      commit('updateGeneration', {
        id,
        data: {
          progress: Math.min(progress, 100),
          progressStartTime: elapsed ? Date.now() - elapsed : generation.progressStartTime,
          isActivelyManaged // Mark as actively managed by VideoGenerator
        }
      });
    },

    // Helper action to mark generation as failed
    failGeneration({ dispatch, state }, { id, message }) {
      dispatch('updateGeneration', {
        id,
        data: {
          status: 'error',
          message: message || 'Generation failed'
        }
      });

      // Check if all generations are completed and stop timers if so
      const hasActiveGenerations = state.generations.some(g => g.status === 'processing' || g.status === 'starting' || (g.progress < 100 && g.status !== 'error'));

      if (!hasActiveGenerations) {
        dispatch('stopProgressTimer');
        dispatch('stopStatusPolling');
      }
    },

    /**
     * Fetch global processing status from server and restore ongoing video generations
     * This allows tracking of video generations even after page reload
     */
    async fetchAndRestoreGlobalProcessing({ commit, dispatch }) {
      try {
        const response = await ContentApi.getGlobalContentProcessing();
        const predictions = response.data?.predictions || [];

        // Filter out failed/error predictions - users don't want to see failed generations on page reload
        const activePredictions = predictions.filter(prediction => {
          // Only restore ongoing processing predictions, not failed/completed ones
          return prediction.status === 'processing' || prediction.status === 'pending' || prediction.status === 'starting';
        });

        // Convert server predictions to video generation format with real-time progress calculation
        const restoredGenerations = activePredictions.map(prediction => {
          const startTime = prediction.created_at || prediction.started_at;
          const startTimeMs = new Date(startTime).getTime();
          const elapsed = Date.now() - startTimeMs;
          const progressDuration = 240000; // 4 minutes in milliseconds (same as VideoGenerator)

          // Calculate real-time progress based on elapsed time (same logic as VideoGenerator)
          let progress = (elapsed / progressDuration) * 100;
          if (progress >= 95 && prediction.status === 'processing') {
            // Slow down progress significantly after 95% to avoid reaching 100% while still generating
            const overtime = elapsed - progressDuration * 0.95;
            const slowProgress = 95 + (overtime / (progressDuration * 0.5)) * 4; // Very slow progress from 95% to 99%
            progress = Math.min(slowProgress, 99);
          } else {
            progress = Math.min(progress, 95);
          }

          return {
            id: prediction.id,
            predictionId: prediction.id,
            fileName: prediction.filename || prediction.file_name || 'AI Video Generation',
            cameraEffect: prediction.camera_effect || 'Video Generation',
            imageUrl: prediction.preview_url,
            placeholderUrl: prediction.placeholder_url,
            contentId: prediction.content_id,
            progress: Math.max(0, progress), // Ensure progress is never negative
            status: prediction.status,
            startTime: startTime,
            progressStartTime: startTimeMs, // Store start time in milliseconds for calculations
            message: prediction.status === 'processing' ? 'Generating video...' : 'Starting generation...',
            videoUrl: prediction.output || null,
            isRestored: true // Mark as restored to avoid auto-showing dialog
          };
        });

        // Add each restored generation to the store
        restoredGenerations.forEach(generation => {
          commit('addGeneration', generation);
        });

        // If there are ongoing generations, show the dialog and start progress timer
        const hasOngoingGenerations = restoredGenerations.some(g => g.status === 'processing' || g.status === 'pending');

        if (hasOngoingGenerations) {
          dispatch('showDialog');
          dispatch('startProgressTimer'); // Start real-time progress updates
          dispatch('startStatusPolling'); // Start status polling to detect completions
        }

        return restoredGenerations.length;
      } catch (error) {
        console.error('Failed to fetch global processing status:', error);
        return 0;
      }
    },

    /**
     * Fetch current global processing status and update existing generations
     * This is used for continuous polling to keep the status up to date
     */
    async getGlobalProcessingStatus({ state, commit }) {
      try {
        const response = await ContentApi.getGlobalContentProcessing();
        const predictions = response.data?.predictions || [];

        // Only process active predictions (in progress)
        const activePredictions = predictions.filter(prediction => {
          return prediction.status === 'processing' || prediction.status === 'pending' || prediction.status === 'starting';
        });

        // Check for new generations that aren't in our local store yet
        const localPredictionIds = new Set(state.generations.map(g => g.predictionId));
        const newPredictions = activePredictions.filter(prediction => !localPredictionIds.has(prediction.id));

        // Add new generations to the store (these might have been started from other parts of the app)
        if (newPredictions.length > 0) {
          newPredictions.forEach(prediction => {
            const newGeneration = {
              id: prediction.id,
              predictionId: prediction.id,
              fileName: prediction.filename,
              cameraEffect: prediction.camera_effect,
              imageUrl: prediction.preview_url,
              placeholderUrl: prediction.placeholder_url,
              contentId: prediction.content_id,
              progress: prediction.status,
              status: prediction.status,
              startTime: prediction.created_at || prediction.started_at,
              message: prediction.status === 'processing' ? 'Generating video...' : 'Starting generation...',
              videoUrl: prediction.output || null,
              isRestored: true // Mark as restored to avoid auto-showing dialog
            };
            commit('addGeneration', newGeneration);
          });

          console.log(`Added ${newPredictions.length} new video generation(s) from global status`);
        }

        // Convert to status update format for existing generations
        const statusUpdates = activePredictions.map(prediction => ({
          predictionId: prediction.id,
          status: prediction.status,
          output: prediction.output,
          error: prediction.error,
          metrics: prediction.metrics,
          created_at: prediction.created_at,
          started_at: prediction.started_at,
          completed_at: prediction.completed_at
        }));

        // Also check for completed/failed generations that are no longer in the active list
        const activePredictionIds = new Set(activePredictions.map(p => p.id));
        const localActiveGenerations = state.generations.filter(g => g.predictionId && g.status !== 'error' && g.progress < 100);

        // For local generations not found in server response, they might have completed or failed
        const missingGenerations = localActiveGenerations.filter(g => !activePredictionIds.has(g.predictionId));

        // Check status of missing generations individually
        const missingStatusUpdates = await Promise.all(
          missingGenerations.map(async generation => {
            try {
              const response = await ReplicateApi.getPredictionStatus(generation.predictionId);
              return {
                predictionId: generation.predictionId,
                status: response.data.prediction?.status,
                output: response.data.prediction?.output,
                error: response.data.prediction?.error
              };
            } catch (error) {
              console.error(`Error checking status for generation ${generation.id}:`, error);
              return {
                predictionId: generation.predictionId,
                status: 'error',
                error: 'Failed to check status'
              };
            }
          })
        );

        // Combine both status update arrays
        const allStatusUpdates = [...statusUpdates, ...missingStatusUpdates];
        return allStatusUpdates.filter(update => update !== null);
      } catch (error) {
        console.error('Error in getGlobalProcessingStatus:', error);
        return [];
      }
    },

    // Start status polling to check for generation completions
    startStatusPolling({ dispatch, state }) {
      // Clear any existing interval
      if (state.statusPollingTimer) {
        clearInterval(state.statusPollingTimer);
        state.statusPollingTimer = null;
      }

      // Start status polling (check every 5 seconds)
      state.statusPollingTimer = setInterval(async () => {
        try {
          const statusUpdates = await dispatch('getGlobalProcessingStatus');
          if (statusUpdates && statusUpdates.length > 0) {
            dispatch('updateGenerationsFromStatus', statusUpdates);
          }
        } catch (error) {
          console.error('Error during status polling:', error);
        }
      }, 5000);
    },

    // Stop status polling
    stopStatusPolling({ state }) {
      if (state.statusPollingTimer) {
        clearInterval(state.statusPollingTimer);
        state.statusPollingTimer = null;
      }
    }
  },

  getters: {
    // Return all generations - cleanup is now only on mount/refresh
    generations: state => state.generations,
    allGenerations: state => state.generations, // Access to all generations for internal use
    isVisible: state => state.isVisible,
    activeGenerations: state => state.generations.filter(g => g.progress < 100 && g.status !== 'error'),
    completedGenerations: state => state.generations.filter(g => g.progress >= 100),
    hasActiveGenerations: state => state.generations.some(g => g.progress < 100 && g.status !== 'error'),
    totalGenerations: state => state.generations.length,
    lastUpdate: state => state.lastUpdate
  }
};

export default videoGenerationProgress;
